/**
 * Capacitor platform detection and native plugin utilities.
 * Used to conditionally disable PWA features in native context
 * and initialize native plugins (StatusBar, SplashScreen).
 */

export function isNativeApp(): boolean {
  const cap = (window as any).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

export function isCapacitor(): boolean {
  return !!(window as any).Capacitor;
}

export function getPlatform(): 'ios' | 'android' | 'web' {
  const cap = (window as any).Capacitor;
  if (!cap) return 'web';
  return cap.getPlatform?.() ?? 'web';
}

/**
 * Initialize native plugins (StatusBar + SplashScreen).
 * Only runs in native Capacitor context, no-op on web.
 */
export async function initNativePlugins(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const platform = getPlatform();

    // Configure StatusBar
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    if (platform === 'ios') {
      // iOS: overlay webview so safe-area CSS handles spacing; default style = dark icons on light bg
      try { await StatusBar.setOverlaysWebView({ overlay: true }); } catch {}
      await StatusBar.setStyle({ style: Style.Default });
    } else if (platform === 'android') {
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }

    // Configure Keyboard plugin (iOS)
    if (platform === 'ios') {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        Keyboard.setAccessoryBarVisible({ isVisible: true });
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[Capacitor] Keyboard plugin init failed:', e);
      }
    }

    // Hide SplashScreen after app is ready
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[Capacitor] Failed to init native plugins:', e);
  }
}

/**
 * Setup deep links listener for Universal Links / App Links.
 * Intercepts URLs opened via email links (password reset, confirmation)
 * and routes them inside the WebView instead of opening Safari.
 */
export async function setupDeepLinks(navigate: (path: string) => void): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { App } = await import('@capacitor/app');

    App.addListener('appUrlOpen', (data) => {
      try {
        const url = new URL(data.url);
        const internalPath = url.pathname + url.search + url.hash;
        if (import.meta.env.DEV) console.info('[DeepLink] Navigating to:', internalPath);
        navigate(internalPath);
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[DeepLink] Failed to parse URL:', data.url, e);
      }
    });

    if (import.meta.env.DEV) console.info('[Capacitor] Deep links listener registered');
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[Capacitor] Failed to setup deep links:', e);
  }
}

/**
 * Update StatusBar color (useful for dark/light theme switching).
 */
/**
 * Open an external URL safely across web and native platforms.
 * In native Capacitor context, uses @capacitor/browser (system browser).
 * On web, falls back to window.open.
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isNativeApp()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[Capacitor] Browser.open failed, falling back:', e);
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function setStatusBarColor(color: string, darkStyle = false): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const platform = getPlatform();
    if (platform === 'ios') {
      // On iOS, keep Default unless explicitly dark mode requested
      await StatusBar.setStyle({ style: darkStyle ? Style.Dark : Style.Default });
    } else if (platform === 'android') {
      await StatusBar.setStyle({ style: darkStyle ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({ color });
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[Capacitor] Failed to set status bar color:', e);
  }
}
